import React, { useState, useEffect, useContext } from "react";
import { useLocation } from "react-router-dom";
import { FaEdit, FaTrash, FaPlus, FaSearch, FaChevronLeft, FaChevronRight, FaFilter, FaTimes, FaCalendarAlt } from "react-icons/fa";
import { monthKeyOf, monthLabel, monthOptions, sortByDateDesc } from "../../utils/monthGroups";
import "./AdminList.css";
import { AuthContext } from "../../context/Authcontext";
import { sectionFromPath } from "../../utils/permissions";

const normalizeCollection = (result) => {
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.items)) return result.items;
  if (Array.isArray(result?.data)) return result.data;
  return [];
};

const getSearchableValue = (value) => {
  if (value == null) return "";
  if (Array.isArray(value)) {
    return value.map(getSearchableValue).join(" ");
  }
  if (typeof value === "object") {
    return Object.values(value).map(getSearchableValue).join(" ");
  }
  return String(value);
};

const renderCellValue = (value) => {
  if (value == null || value === "") return "-";
  if (Array.isArray(value)) {
    const rendered = value.map(getSearchableValue).filter(Boolean).join(", ");
    return rendered || "-";
  }
  if (typeof value === "object") {
    const rendered = Object.values(value).map(getSearchableValue).filter(Boolean).join(" • ");
    return rendered || "-";
  }
  return value;
};

export default function AdminList({
  title,
  fetchData,
  deleteData,
  columns,
  onEdit,
  onAdd,
  onView,
  searchPlaceholder = "Search...",
  filters = [],
  customActions,
  defaultItemsPerPage = 10,
  permissionModule,
  canModifyItem, // optional (item) => boolean; false hides edit/delete for that row
  // optional: group rows per month and filter by month.
  // { options: [{ key, label, getDate: (item) => date }] } - first option is the default.
  monthGrouping,
}) {
  // Hide actions the logged-in account is not allowed to perform. The module is
  // derived from the URL (/admin/<section>) unless passed explicitly.
  const location = useLocation();
  const { can } = useContext(AuthContext);
  const moduleKey = permissionModule || sectionFromPath(location.pathname)?.module;
  const allowed = (action) => !moduleKey || can(`${moduleKey}.${action}`);
  const canCreate = allowed("create");
  const canUpdate = allowed("update");
  const canDelete = allowed("delete") && typeof deleteData === "function";
  // The edit button also opens the read-only detail view, so keep it when there is no separate view button.
  const showEdit = onEdit && (canUpdate || !onView);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});
  const monthBases = monthGrouping?.options || [];
  const [monthBasis, setMonthBasis] = useState(monthBases[0]?.key || null);
  const [monthFilter, setMonthFilter] = useState("");
  const [groupByMonth, setGroupByMonth] = useState(Boolean(monthBases.length));
  const activeBasis = monthBases.find((b) => b.key === monthBasis) || monthBases[0];
  const getMonthDate = (item) => (activeBasis ? activeBasis.getDate(item) : null);

  const loadData = async () => {
    try {
      setLoading(true);
      const result = await fetchData();
      setData(normalizeCollection(result));
      setError(null);
    } catch (err) {
      console.error("Failed to load data:", err);
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      try {
        await deleteData(id);
        loadData();
      } catch (err) {
        console.error("Failed to delete:", err);
        alert(err?.message || "Failed to delete item");
      }
    }
  };

  const handleFilterChange = (filterKey, value) => {
    setActiveFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setActiveFilters({});
    setCurrentPage(1);
  };

  const hasActiveFilters = Object.values(activeFilters).some(v => v && v !== "");

  // Filter data based on search and filters
  const filteredData = data.filter((item) => {
    const searchLower = searchTerm.toLowerCase();
    
    // Search filter
    const matchesSearch = !searchTerm || columns.some((col) => {
      const value = getSearchableValue(item[col.key]);
      return value && value.toLowerCase().includes(searchLower);
    });

    // Custom filters
    const matchesFilters = filters.every(filter => {
      const filterValue = activeFilters[filter.key];
      if (!filterValue || filterValue === "") return true;
      
      const itemValue = item[filter.key];
      
      if (filter.type === "select") {
        return itemValue === filterValue;
      }

      if (filter.type === "search") {
        return getSearchableValue(itemValue).toLowerCase().includes(String(filterValue).toLowerCase());
      }
      
      if (filter.type === "date") {
        const itemDate = new Date(itemValue);
        const filterDate = new Date(filterValue);
        return itemDate.toDateString() === filterDate.toDateString();
      }
      
      if (filter.type === "dateRange") {
        const itemDate = new Date(itemValue);
        const start = filterValue.start ? new Date(filterValue.start) : null;
        const end = filterValue.end ? new Date(filterValue.end) : null;
        if (start && end) {
          return itemDate >= start && itemDate <= end;
        } else if (start) {
          return itemDate >= start;
        } else if (end) {
          return itemDate <= end;
        }
        return true;
      }
      
      return true;
    });

    return matchesSearch && matchesFilters;
  });

  // Month filter + grouping (newest month first).
  const monthChoices = activeBasis ? monthOptions(filteredData, getMonthDate) : [];
  let listData = filteredData;
  if (activeBasis && monthFilter) listData = listData.filter((item) => monthKeyOf(getMonthDate(item)) === monthFilter);
  if (activeBasis && groupByMonth) listData = sortByDateDesc(listData, getMonthDate);
  const monthCounts = new Map();
  if (activeBasis) listData.forEach((item) => {
    const k = monthKeyOf(getMonthDate(item));
    monthCounts.set(k, (monthCounts.get(k) || 0) + 1);
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = listData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(listData.length / itemsPerPage);

  if (loading) {
    return (
      <div className="admin-list-loading">
        <div className="spinner"></div>
        <p>Loading {title}...</p>
      </div>
    );
  }

  if (error) {
    return <div className="admin-list-error">{error}</div>;
  }

  return (
    <div className="admin-list-container">
      <div className="admin-list-header">
        <h2>{title}</h2>
        <div className="header-actions">
          {filters.length > 0 && (
            <button 
              className={`filter-toggle-btn ${showFilters ? 'active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <FaFilter /> Filters
              {hasActiveFilters && <span className="filter-badge">{Object.keys(activeFilters).filter(k => activeFilters[k]).length}</span>}
            </button>
          )}
          {canCreate && onAdd && (
            <button className="add-btn" onClick={onAdd}>
              <FaPlus /> Add New
            </button>
          )}
        </div>
      </div>

      {filters.length > 0 && showFilters && (
        <div className="filters-panel">
          <div className="filters-grid">
            {filters.map(filter => (
              <div key={filter.key} className="filter-item">
                <label>{filter.label}</label>
                {filter.type === "select" && (
                  <select 
                    value={activeFilters[filter.key] || ""}
                    onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                  >
                    <option value="">All</option>
                    {filter.options.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                )}
                {filter.type === "date" && (
                  <input 
                    type="date"
                    value={activeFilters[filter.key] || ""}
                    onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                  />
                )}
                {filter.type === "dateRange" && (
                  <div className="date-range-inputs">
                    <input 
                      type="date"
                      value={activeFilters[filter.key]?.start || ""}
                      onChange={(e) => handleFilterChange(filter.key, { 
                        ...activeFilters[filter.key], 
                        start: e.target.value 
                      })}
                      placeholder="Start date"
                    />
                    <span style={{ margin: "0 5px" }}>to</span>
                    <input 
                      type="date"
                      value={activeFilters[filter.key]?.end || ""}
                      onChange={(e) => handleFilterChange(filter.key, { 
                        ...activeFilters[filter.key], 
                        end: e.target.value 
                      })}
                      placeholder="End date"
                    />
                  </div>
                )}
                {filter.type === "search" && (
                  <input 
                    type="text"
                    placeholder={filter.placeholder}
                    value={activeFilters[filter.key] || ""}
                    onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                  />
                )}
              </div>
            ))}
          </div>
          {hasActiveFilters && (
            <button className="clear-filters-btn" onClick={clearFilters}>
              <FaTimes /> Clear Filters
            </button>
          )}
        </div>
      )}

      {activeBasis && (
        <div className="month-bar">
          <FaCalendarAlt className="month-bar-icon" />
          {monthBases.length > 1 && (
            <label>
              Datum
              <select value={activeBasis.key} onChange={(e) => { setMonthBasis(e.target.value); setMonthFilter(""); setCurrentPage(1); }}>
                {monthBases.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
              </select>
            </label>
          )}
          <label>
            Maand
            <select value={monthFilter} onChange={(e) => { setMonthFilter(e.target.value); setCurrentPage(1); }}>
              <option value="">Alle maanden</option>
              {monthChoices.map((m) => <option key={m.key} value={m.key}>{m.label} ({m.count})</option>)}
            </select>
          </label>
          <label className="month-bar-check">
            <input type="checkbox" checked={groupByMonth} onChange={(e) => { setGroupByMonth(e.target.checked); setCurrentPage(1); }} />
            Groeperen per maand
          </label>
          {monthFilter && (
            <button type="button" className="clear-filters-btn" onClick={() => setMonthFilter("")}>
              <FaTimes /> Alle maanden
            </button>
          )}
        </div>
      )}

      <div className="search-bar">
        <FaSearch className="search-icon" />
        <input
          type="text"
          placeholder={searchPlaceholder}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
        />
        {searchTerm && (
          <button className="clear-search" onClick={() => setSearchTerm("")}>
            <FaTimes />
          </button>
        )}
      </div>

      <div className="results-info">
        <div className="results-count">
          Showing {currentItems.length} of {listData.length} {title.toLowerCase()}
        </div>
        <div className="items-per-page">
          <label>Per page: </label>
          <select 
            value={itemsPerPage} 
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            {[10, 25, 50, 100, 250, 500, 1000].map(num => (
              <option key={num} value={num}>{num}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="table-container">
        <table className="admin-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.key}>{col.label}</th>
              ))}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="no-data">
                  No data available
                </td>
              </tr>
            ) : (
              currentItems.map((item, rowIndex) => {
                const rowMonth = activeBasis && groupByMonth ? monthKeyOf(getMonthDate(item)) : null;
                const prevMonth = rowMonth && rowIndex > 0 ? monthKeyOf(getMonthDate(currentItems[rowIndex - 1])) : null;
                const showMonthHeader = Boolean(rowMonth) && rowMonth !== prevMonth;
                return (
                <React.Fragment key={item._id || item.id || JSON.stringify(item)}>
                {showMonthHeader && (
                  <tr className="month-row">
                    <td colSpan={columns.length + 1}>
                      <FaCalendarAlt /> {monthLabel(rowMonth)}
                      <span className="month-row-count">{monthCounts.get(rowMonth) || 0}</span>
                    </td>
                  </tr>
                )}
                <tr>
                  {columns.map((col) => (
                    <td key={col.key}>
                      {col.render ? col.render(item[col.key], item) : renderCellValue(item[col.key])}
                    </td>
                  ))}
                  <td className="actions">
                    {onView && (
                      <button
                        className="view-btn"
                        onClick={() => onView(item)}
                        title="View"
                      >
                        <FaSearch />
                      </button>
                    )}
                    {showEdit && (canModifyItem ? canModifyItem(item) || !onView : true) && (
                      <button
                        className="edit-btn"
                        onClick={() => onEdit(item)}
                        title="Edit"
                      >
                        <FaEdit />
                      </button>
                    )}
                    {customActions && customActions(item)}
                    {canDelete && (!canModifyItem || canModifyItem(item)) && (
                      <button
                        className="delete-btn"
                        onClick={() => handleDelete(item._id || item.id)}
                        title="Delete"
                      >
                        <FaTrash />
                      </button>
                    )}
                  </td>
                </tr>
                </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {listData.length > itemsPerPage && (
        <div className="pagination">
          <button
            className="page-btn"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            <FaChevronLeft />
          </button>
          <div className="page-numbers">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  className={`page-num ${currentPage === pageNum ? 'active' : ''}`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          <button
            className="page-btn"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            <FaChevronRight />
          </button>
        </div>
      )}
    </div>
  );
}
